/**
 * The settings page's header — the same one the account and location
 * dashboards show, with Settings as the active tab:
 *
 *   All accounts › Splits Demo Motors › Express Lane › Settings
 *   [logo] Express Lane ▾
 *   Dashboard | Settings
 *
 * Every way out (a breadcrumb, the Dashboard tab, the account or location
 * switcher) leaves settings first, asking about unsaved edits.
 */
import React from 'react';
import { Building2, Store } from 'lucide-react';
import { EntityLogo, tokenVars } from '@turn-connect/ui';
import { buildNavTrail, type User } from '@turn-connect/shared';
import { usePermissions } from '@turn-connect/hooks';
import { navigateToAccountDashboard } from '../../../utils/navigation';
import { openSettings } from '../../../utils/settingsDialogs';
import { leaveSettingsAndGo } from '../../../hooks/useSettingsDialogUrl';
import { adminService } from '../../../services/auth';
import { leaveSettingsTo, trailHref } from '../../navigation/place/placeNavigation';
import { useAccountName, useAccountSwitchMenu, usePlaceAccess } from '../../navigation/place/usePlaceAccess';
import { PlaceHeader, type PlaceHeaderProps } from '../../navigation/place/PlaceHeader';
import { LocationSwitcher } from '../scope/LocationSwitcher';

export interface SettingsHeaderInput {
  kind: 'account' | 'location';
  user: User | null | undefined;
  permissions: string[];
  accountId?: string | null;
  accountName?: string | null;
  /** Location settings only. */
  locationId?: string | null;
  locationName?: string | null;
  /** Location settings: open another location's settings (the host leaves this page first). */
  onSwitchLocation?: (locationId: string) => void;
  /** Location settings: the host's own list, when the server finds none for this viewer. */
  fallbackLocations?: Array<{ id: string; name: string }>;
}

/** What SettingsPageFrame draws as its header (PlaceHeaderProps with Settings as the active tab). */
export type SettingsHeader = Omit<PlaceHeaderProps, 'tabs'> & {
  /** The Dashboard tab; null when the person may not open the place's dashboard. */
  dashboard: { href?: string; onOpen: () => void } | null;
};

const leaveAll = leaveSettingsAndGo;

const logoBox = (child: React.ReactNode) => (
  <span
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 48,
      height: 48,
      borderRadius: 10,
      background: tokenVars.surface,
      overflow: 'hidden',
    }}
  >
    {child}
  </span>
);

export function useSettingsHeader(input: SettingsHeaderInput): SettingsHeader | null {
  const { kind, user, permissions, accountId, locationId, locationName, onSwitchLocation, fallbackLocations } = input;
  const access = usePlaceAccess(user, permissions);
  // Who may open the account's settings (the Accounts page's Settings tab gate).
  const { canRead: canReadAccount, canUpdate: canUpdateAccount } = usePermissions(user as never, permissions, 'accounts');
  // The name the person's own account list knows beats a host's placeholder.
  const accountName = useAccountName(user, permissions, accountId) || input.accountName;
  const accountMenu = useAccountSwitchMenu({
    user,
    permissions,
    accountId,
    before: leaveAll,
    go: {
      allAccounts: () => leaveSettingsTo({ kind: 'accounts' }),
      // The dashboard underneath moves too, so Dashboard and Back stay on the new account.
      account: (id) => {
        navigateToAccountDashboard(id);
        openSettings({ kind: 'account', accountId: id });
      },
    },
  });
  if (kind === 'location' ? !locationId : !accountId) return null;

  const isLocation = kind === 'location';
  const crumbs = buildNavTrail({
    canSeeAllAccounts: access.canSeeAllAccounts,
    account: accountId ? { id: accountId, name: accountName || 'Account', canOpen: access.canOpenAccount } : null,
    location: isLocation && locationId ? { id: locationId, name: locationName || 'Location', canOpen: access.canOpenLocation } : null,
    settings: true,
    // A location's settings hang under the account's for someone who may open
    // those: Account › Settings › Location, the way back always one tap.
    // (Opened from the account's Locations & groups, that is where it returns.)
    accountSettings: isLocation && accountId && (canReadAccount || canUpdateAccount) ? { section: 'locations' } : null,
  });
  // The Dashboard tab goes to this place's dashboard — only for someone who may open it.
  const dashboardTarget =
    isLocation && accountId
      ? access.canOpenLocation
        ? ({ kind: 'location', accountId, locationId: locationId! } as const)
        : null
      : accountId && access.canOpenAccount
        ? ({ kind: 'account', accountId } as const)
        : null;

  return {
    crumbs,
    onNavigate: (target) => leaveSettingsTo(target, { isSystemAdmin: access.isSystemAdmin }),
    logo: logoBox(
      isLocation ? (
        <EntityLogo
          src={adminService.getLocationLogoUrl(locationId!)}
          height={40}
          alt=""
          placeholder={<Store size={22} color={tokenVars.text.muted} />}
        />
      ) : (
        <EntityLogo
          src={adminService.getAccountLogoUrl(accountId!)}
          height={40}
          alt=""
          placeholder={<Building2 size={22} color={tokenVars.text.muted} />}
        />
      )
    ),
    title: (isLocation ? locationName : accountName) || (isLocation ? 'Location' : 'Account'),
    switchMenu: isLocation ? null : accountMenu,
    titleNode:
      isLocation && accountId && locationName && onSwitchLocation ? (
        <h1 className="ph-title">
          <LocationSwitcher
            variant="title"
            accountId={accountId}
            current={{ id: locationId!, name: locationName }}
            onSwitch={onSwitchLocation}
            fallbackLocations={fallbackLocations}
          />
        </h1>
      ) : undefined,
    subline: isLocation ? accountName || undefined : undefined,
    dashboard: dashboardTarget
      ? { href: trailHref(dashboardTarget), onOpen: () => leaveSettingsTo(dashboardTarget, { isSystemAdmin: access.isSystemAdmin }) }
      : null,
  };
}

/**
 * The settings page's header bar: the shared page header with Settings as
 * the active tab. Rendered by SettingsPageFrame for the page in front.
 */
export const SettingsPlaceHeader: React.FC<{ input: SettingsHeaderInput; compact: boolean; onBack: () => void }> = ({
  input,
  compact,
  onBack,
}) => {
  const header = useSettingsHeader(input);
  // Before the place is known: a placeholder title, never an empty heading.
  const { dashboard, ...rest } = header ?? { dashboard: { onOpen: onBack }, crumbs: [], title: 'Loading…' };
  return (
    <PlaceHeader
      {...rest}
      compact={compact}
      // No Dashboard to go to: no tab row (the breadcrumbs and the main navigation remain).
      tabs={dashboard ? { active: 'settings', dashboard, settings: { onOpen: () => undefined } } : null}
      testId="settings-page-header"
    />
  );
};
